import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Receipt,
  Calendar,
  Wrench,
  QrCode,
  Package,
  Bell,
  Users,
  Building2,
  Droplets,
  FileText,
  DollarSign,
  MoreHorizontal,
} from 'lucide-react';

/**
 * Bottom tab navigation for mobile viewports (< md).
 * Shows up to 4 role-specific primary destinations + a "More" tab that opens
 * the existing sidebar drawer (via the parent's setSidebarOpen prop).
 */
export const MobileBottomNav = ({ onMore }) => {
  const { user } = useAuth();
  const location = useLocation();

  const itemsByRole = () => {
    const base = { path: '/dashboard', label: 'Home', icon: LayoutDashboard };
    switch (user?.role) {
      case 'resident':
        return [
          base,
          { path: '/invoices', label: 'Invoices', icon: Receipt },
          { path: '/facilities', label: 'Book', icon: Calendar },
          { path: '/announcements', label: 'News', icon: Bell },
        ];
      case 'management':
        return [
          base,
          { path: '/invoices', label: 'Invoices', icon: Receipt },
          { path: '/maintenance', label: 'Tickets', icon: Wrench },
          { path: '/reports', label: 'Reports', icon: FileText },
        ];
      case 'security':
        return [
          base,
          { path: '/visitors', label: 'Scan', icon: QrCode },
          { path: '/parcels', label: 'Parcels', icon: Package },
        ];
      case 'supervisor':
        return [
          base,
          { path: '/maintenance', label: 'Tickets', icon: Wrench },
        ];
      case 'owner':
        return [
          base,
          { path: '/payments', label: 'Pay', icon: DollarSign },
          { path: '/facilities', label: 'Facilities', icon: Calendar },
          { path: '/reports', label: 'Reports', icon: FileText },
        ];
      default:
        return [base];
    }
  };

  const items = itemsByRole();

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#e2e8f0] pb-[env(safe-area-inset-bottom)]"
      data-testid="mobile-bottom-nav"
    >
      <ul className="grid grid-cols-5 h-16">
        {items.map((item) => {
          const active = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <li key={item.path} className="flex">
              <Link
                to={item.path}
                className={`flex-1 flex flex-col items-center justify-center gap-1 text-[10px] font-medium transition-colors ${
                  active ? 'text-[#2563eb]' : 'text-[#64748b] hover:text-[#0f172a]'
                }`}
                data-testid={`mobile-nav-${item.path.slice(1) || 'root'}`}
              >
                <Icon className={`h-5 w-5 ${active ? 'stroke-[2.5]' : ''}`} />
                <span className="leading-none">{item.label}</span>
              </Link>
            </li>
          );
        })}
        {/* Fill remaining columns with "More" */}
        {Array.from({ length: Math.max(0, 4 - items.length) }).map((_, i) => (
          <li key={`spacer-${i}`} className="flex" />
        ))}
        <li className="flex">
          <button
            type="button"
            onClick={onMore}
            className="flex-1 flex flex-col items-center justify-center gap-1 text-[10px] font-medium text-[#64748b] hover:text-[#0f172a]"
            data-testid="mobile-nav-more"
          >
            <MoreHorizontal className="h-5 w-5" />
            <span className="leading-none">More</span>
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default MobileBottomNav;
